// ── SellerSidebar.jsx ─────────────────────────────────────────────────────────
// Right column: seller profile card, safety tips, seller's other items
//
// ✅ FIX 1: isFollowLoading wired — disables Follow button during API call
// ✅ FIX 2: Offline-safe SVG image fallback (no via.placeholder.com)
// ✅ FIX 3: Response time pulled from backend (fallback "N/A")
// ✅ FIX 4: "Member for X" computed from real join date (not hardcoded)
// ✅ FIX 5: Verification badge pulled from verificationStatus field
// ✅ FIX 6: Rating shows real reviewCount (not hardcoded 12)
// ✅ FIX 7: Verified badge shows '✅ Account Verified'; name uses first+last name

import React, { useState, useEffect } from 'react';
import styles from '../ProductDetailModal.module.css';
import apiService from '../../../../services/api';

// ── Offline-safe image fallback ───────────────────────────────────────────────
const FALLBACK_IMAGE_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' " +
  "viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E" +
  "%3Crect x='75' y='55' width='50' height='40' rx='4' fill='%23ccc'/%3E" +
  "%3Ccircle cx='88' cy='72' r='6' fill='%23aaa'/%3E" +
  "%3Cpolygon points='75,95 93,75 108,85 123,68 138,95' fill='%23bbb'/%3E" +
  "%3Ctext x='100' y='130' font-family='Arial' font-size='11' fill='%23999' " +
  "text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

// ── Helpers ───────────────────────────────────────────────────────────────────
const VIDEO_EXTS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
const isVideo = (url) => Boolean(url) && VIDEO_EXTS.some(ext => url.toLowerCase().includes(ext));

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(part => part[0]).join('').toUpperCase().slice(0, 2);
};

// Compute "Member for X years / X months" from a raw ISO date string
const getMemberDuration = (rawDate) => {
  if (!rawDate) return null;
  try {
    const joined  = new Date(rawDate);
    if (isNaN(joined.getTime())) return null;
    const now     = new Date();
    const months  = (now.getFullYear() - joined.getFullYear()) * 12 + (now.getMonth() - joined.getMonth());
    if (months < 1)  return 'New member';
    if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
    const years   = Math.floor(months / 12);
    const rem     = months % 12;
    if (rem === 0)   return `${years} year${years === 1 ? '' : 's'}`;
    return `${years}y ${rem}m`;
  } catch {
    return null;
  }
};

// ✅ FIX 7: Map verificationStatus → badge label + colour
// 'verified' now shows '✅ Account Verified' in green to match admin panel
const getVerificationBadge = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'verified':
      return { label: '✅ Account Verified', color: '#059669', bg: '#d1fae5' };
    case 'partial':
      return { label: '~ Partial',            color: '#d97706', bg: '#fef3c7' };
    case 'pending':
      return { label: '⏳ Under Review',       color: '#2563eb', bg: '#dbeafe' };
    default:
      return { label: 'Unverified',            color: '#6b7280', bg: '#f3f4f6' };
  }
};

// ✅ FIX 7: Resolve display name — prefer first+last name, fall back to
// seller_name (which may be an email), then the raw email, then 'Seller'.

// Masks an email fallback: strips trailing digits and the @domain part.
// e.g. "kiberober65@gmail.com" → "kiberober"
//      "john.doe@mail.com"    → "john.doe"
// If the value is not an email it is returned as-is.
const maskEmailFallback = (raw) => {
  if (!raw || !raw.includes('@')) return raw;
  const localPart = raw.split('@')[0];          // everything before @
  return localPart.replace(/\d+$/, '');         // strip trailing digits
};

const resolveSellerName = (product) => {
  const first = product?.seller_first_name || product?.first_name || '';
  const last  = product?.seller_last_name  || product?.last_name  || '';
  const full  = `${first} ${last}`.trim();
  if (full) return full;
  // Fall back to seller field — mask if it looks like an email
  const raw = product?.seller || product?.sellerName || '';
  if (raw) return maskEmailFallback(raw);
  return 'Seller';
};

// ─────────────────────────────────────────────────────────────────────────────

const resolveUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.includes('%3A') || url.includes('%2F')) return null;
  const host = 'https://imselling-marketplace-production.up.railway.app';
  return `${host}${url.startsWith('/') ? '' : '/'}${url}`;
};

const SellerSidebar = ({
  product,
  sellerAvatarUrl,
  loggedInUser,
  isFollowing,
  followCount,
  sellerOtherProducts,
  onFollowToggle,
  onRelatedProductClick,
  onReportSeller,
  isFollowLoading,
  authProfileImage,
}) => {

  // ── Derived seller info ─────────────────────────────────────────────────
  const memberDuration  = getMemberDuration(product?.sellerJoinedRaw);
  const verif           = getVerificationBadge(product?.verificationStatus);
  const responseTime    = product?.responseTime || 'N/A';
  const reviewCount     = product?.reviewCount  || 0;
  const rating          = product?.rating       || 0;

  // ✅ FIX 7: Use real first+last name instead of raw email/username
  const sellerDisplayName = resolveSellerName(product);

  // ── Seller avatar — exact same fetch chain as ProductInfoSection ──────────
  const [resolvedAvatar, setResolvedAvatar] = useState(sellerAvatarUrl || null);

  const sellerIdentifier =
    product?.email        ||
    product?.seller_email ||
    product?.seller       ||
    product?.sellerName   ||
    null;

  useEffect(() => {
    const resolve = (url) => resolveUrl(url);

    const probeImage = (url) =>
      new Promise((res) => {
        const img = new Image();
        img.onload  = () => res(true);
        img.onerror = () => res(false);
        img.src = url;
      });

    const resolveSellerImage = async () => {
      // Step 1: Is the logged-in user the seller?
      const loggedInUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
      })();
      const loggedInEmail = (loggedInUser?.email || loggedInUser?.identifier || '').toLowerCase().trim();
      const sellerEmail   = (product?.email || product?.seller_email || '').toLowerCase().trim();

      if (loggedInEmail && sellerEmail && loggedInEmail === sellerEmail) {
        if (authProfileImage) { setResolvedAvatar(authProfileImage); return; }
        return;
      }

      // Step 2: Product-embedded fields
      const productCandidates = [
        resolve(product?.seller_profile_image_url),
        resolve(product?.seller_profile_image),
        resolve(product?.seller_avatar_url),
        resolve(product?.seller_avatar),
      ].filter(Boolean);

      for (const candidate of productCandidates) {
        if (candidate.includes('default') || candidate.includes('placeholder')) continue;
        const ok = await probeImage(candidate);
        if (ok) { setResolvedAvatar(candidate); return; }
      }

      // Step 3: Auth-token fetch intentionally removed.
      // Fetching /auth/profile/ with the logged-in token returns the CURRENT
      // user's photo, not the seller's — causing the wrong avatar to appear
      // on ads posted by other users. Skip straight to the public API.

      // Step 4: Public seller-profile API
      if (!sellerIdentifier) return;
      try {
        const data = await apiService.getSellerProfile?.(sellerIdentifier);
        if (data) {
          const img = resolve(data?.profile_image_url || data?.profile_image);
          if (img) setResolvedAvatar(img);
        }
      } catch (_) { /* fall back to initials */ }
    };

    resolveSellerImage();
  }, [sellerIdentifier, product, authProfileImage, sellerAvatarUrl]);

  return (
    <div className={styles.rightSidebar}>

      {/* ── Seller profile card ─────────────────────────────────────────── */}
      <div className={styles.sellerProfileCard}>
        <div className={styles.sellerHeader}>
          <div className={styles.sellerAvatar}>
            {resolvedAvatar ? (
              <img
                src={resolvedAvatar}
                alt={sellerDisplayName}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={styles.sellerAvatarInitials}
              style={{ display: resolvedAvatar ? 'none' : 'flex' }}
            >
              {getInitials(sellerDisplayName)}
            </div>
            {product.verified && <div className={styles.verifiedBadge}>✓</div>}
          </div>

          <div className={styles.sellerInfo}>
            {/* ✅ FIX 7: Show real name, not email */}
            <h4>{sellerDisplayName}</h4>
            {/* ✅ FIX 5 + 7: Verification badge — 'verified' shows 'Account Verified' */}
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: verif.color,
                background: verif.bg,
                padding: '2px 8px',
                borderRadius: '99px',
                display: 'inline-block',
              }}
            >
              {verif.label}
            </span>
          </div>
        </div>

        {/* ── Follow button ────────────────────────────────────────────── */}
        {loggedInUser !== product.seller && (
          <button
            className={`${styles.followBtn} ${isFollowing ? styles.followingBtn : ''}`}
            onClick={onFollowToggle}
            disabled={isFollowLoading}
            style={{
              opacity:    isFollowLoading ? 0.65 : 1,
              cursor:     isFollowLoading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s',
            }}
          >
            {isFollowLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg
                  width="13" height="13" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  style={{ animation: 'followSpin 0.75s linear infinite' }}
                >
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.2" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                Loading…
              </span>
            ) : isFollowing ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>{' '}Following
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14" />
                </svg>{' '}Follow
              </>
            )}
          </button>
        )}

        {/* ── Seller stats ─────────────────────────────────────────────── */}
        <div className={styles.sellerStats}>

          {/* ✅ FIX 3: Response time from backend */}
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Response time:</span>
            <span className={styles.statValue}>{responseTime}</span>
          </div>

          {/* ✅ FIX: Real rating + real review count */}
          <div className={styles.statItem}>
            <span className={styles.statLabel}>⭐ Rating:</span>
            <span className={styles.statValue}>
              {rating > 0 ? `${rating} / 5` : 'No ratings yet'}
              {reviewCount > 0 && (
                <span style={{ color: '#6b7280', fontSize: '11px' }}> ({reviewCount})</span>
              )}
            </span>
          </div>

          {/* ✅ FIX 4: Member duration computed from real join date */}
          {memberDuration && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Member for</span>
              <span className={styles.statBadge}>{memberDuration}</span>
            </div>
          )}

          <div className={styles.statItem}>
            <span className={styles.statLabel}>Total listings:</span>
            <span className={styles.statValue}>
              {product.sellerListings || 0}
              {product.verified && (
                <span className={styles.verifiedIcon}> ✓ Verified</span>
              )}
            </span>
          </div>

          <div className={styles.statItem}>
            <span className={styles.statLabel}>👥 Followers:</span>
            <span className={styles.statValue}>{followCount}</span>
          </div>

        </div>
      </div>

      {/* ── Safety tips ─────────────────────────────────────────────────── */}
      <div className={styles.safetyTips}>
        <h4>🛡️ Safety Tips</h4>
        <ul className={styles.safetyList}>
          <li>Meet in public places</li>
          <li>Avoid advance payments</li>
          <li>Inspect before buying</li>
          <li>Verify seller identity</li>
        </ul>
        <button className={styles.reportButton} onClick={onReportSeller}>
          🚩 Report Seller
        </button>
      </div>

      {/* ── Seller's other items ─────────────────────────────────────────── */}
      {sellerOtherProducts.length > 0 && (
        <div className={styles.sellerOtherItems}>
          <h4>Similar Items by Seller <a href="#similar-items">See All</a></h4>
          <div className={styles.sellerItemsGrid}>
            {sellerOtherProducts.slice(0, 4).map((p) => (
              <div
                className={styles.sellerItemCard}
                key={p.id}
                onClick={() => onRelatedProductClick(p)}
              >
                <div className={styles.sellerItemImage}>
                  {/* ✅ Video ads show as <video>, image ads as <img> */}
                  {isVideo(p.img) ? (
                    <video
                      src={p.img}
                      muted
                      loop
                      playsInline
                      onMouseEnter={e => e.currentTarget.play()}
                      onMouseLeave={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover',
                        borderRadius: '6px 6px 0 0',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <img
                      src={p.img || FALLBACK_IMAGE_SVG}
                      alt={p.name}
                      onError={(e) => {
                        if (e.target.src !== FALLBACK_IMAGE_SVG) e.target.src = FALLBACK_IMAGE_SVG;
                      }}
                    />
                  )}
                  {/* Video badge */}
                  {isVideo(p.img) && (
                    <div style={{
                      position: 'absolute', top: 4, right: 4,
                      background: 'rgba(0,0,0,0.65)', color: '#fff',
                      fontSize: '8px', fontWeight: 700, padding: '2px 4px',
                      borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '2px',
                    }}>
                      <svg width="7" height="7" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                      VIDEO
                    </div>
                  )}
                  {/* ✅ FIX 6: Real like count instead of Math.random() */}
                  <div className={styles.sellerItemLikes}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                    {p.like_count ?? p.likes ?? 0}
                  </div>
                </div>
                <div className={styles.sellerItemInfo}>
                  <div className={styles.sellerItemPrice}>KES {p.price?.toLocaleString()}</div>
                  {/* ✅ FIX 6: Real view count */}
                  <div className={styles.sellerItemViews}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    {p.views ?? 0}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes followSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

    </div>
  );
};

export default SellerSidebar;